import { useState } from 'react';
import { Link } from 'react-router-dom';
import useUser from '../auth/useUser';
import DB from '../data/mockData';

const EXPERTS = [
  { av: 'JP', bg: 'var(--purple)', name: 'Jan Pieters — Commercial', note: '"Strong tender position. 60/40 split viable anchor. Competitive window open now."' },
  { av: 'SL', bg: 'var(--amber)', name: 'Sophie Linders — Legal', note: '"NDA reviewed. No IP conflicts. Standard in-licensing terms applicable."' },
  { av: 'MV', bg: 'var(--green)', name: 'Maria van den Berg — Regulatory Affairs', note: '"14-month DCP timeline confirmed. No blocking issues. CBG dossier pre-assessed."' },
];

const FIN_ROWS = [
  { param: 'Market Share', vals: ['12%', '16%', '18%', '19%'] },
  { param: 'Discount', vals: ['18%', '17%', '16%', '15%'] },
  { param: 'CoGS', vals: ['€0,82/unit', '€0,80/unit', '€0,78/unit', '€0,77/unit'] },
  { param: 'RA Costs', vals: ['€48.000', '€12.000', '€8.000', '€6.000'] },
  { param: 'Profit Split', vals: ['60/40', '60/40', '60/40', '60/40'] },
];

const NET_ROW = { param: 'Net Revenue', vals: ['€2.580.000', '€3.420.000', '€3.980.000', '€4.140.000'] };

export default function BCEditor() {
  const { displayName } = useUser();
  const [steps, setSteps] = useState(DB.bcSteps.map(s => ({ ...s })));
  const [openStep, setOpenStep] = useState(4);

  const handleApprove = () => {
    setSteps(prev => prev.map(s => {
      if (s.id === 4) return { ...s, status: 'done' };
      if (s.id === 5) return { ...s, status: 'active' };
      return s;
    }));
    setOpenStep(5);
  };

  return (
    <div className="content">
      {/* Header */}
      <div className="d-flex align-items-center gap-3 flex-wrap">
        <Link to="/bc" className="btn btn-s btn-sm">← BCs</Link>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--navy)', fontFamily: "'Syne',sans-serif" }}>Business Case — Valsartan 160 mg</div>
          <div style={{ fontSize: 11, color: 'var(--t3)' }}>Sandoz NL · Cardiovascular · Nova updated 2h ago with Farminform Q1 2026</div>
        </div>
        <div className="ms-auto d-flex flex-wrap gap-2">
          <span className="ai-badge">✦ Nova-managed</span>
          <button className="btn btn-s btn-sm">↓ Excel</button>
          <button className="btn btn-s btn-sm">↓ PDF</button>
        </div>
      </div>

      {/* Steps accordion */}
      <div className="card">
        <div className="cb d-flex flex-column gap-2">
          {steps.map(s => (
            <StepAccordion
              key={s.id}
              step={s}
              isOpen={openStep === s.id}
              onToggle={() => setOpenStep(openStep === s.id ? null : s.id)}
              onApprove={handleApprove}
              userName={displayName}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function StepAccordion({ step: s, isOpen, onToggle, onApprove, userName }) {
  const cls = s.status === 'done' ? 's-done' : s.status === 'active' ? 's-active' : 's-locked';
  const statusPill = s.status === 'done'
    ? <span className="pill p-g">✓ Approved</span>
    : s.status === 'active'
    ? <span className="pill p-a">⚡ Awaiting MD</span>
    : <span className="pill p-n">🔒 Locked</span>;

  return (
    <div className={`step ${cls}`} style={{
      border: '1px solid var(--brd)',
      borderRadius: 'var(--r2)',
      overflow: 'hidden',
      opacity: s.status === 'locked' ? .45 : 1,
      ...(s.status === 'done' ? { borderColor: 'var(--green)' } : {}),
      ...(s.status === 'active' ? { borderColor: 'var(--teal)', boxShadow: '0 0 0 3px rgba(26,138,175,.1)' } : {}),
    }}>
      {/* Header */}
      <div
        className="d-flex align-items-center gap-3 p-3"
        style={{
          cursor: s.status !== 'locked' ? 'pointer' : 'default',
          background: s.status === 'done' ? 'var(--green2)' : s.status === 'active' ? 'var(--teal3)' : 'var(--surf)',
        }}
        onClick={() => s.status !== 'locked' && onToggle()}
      >
        <div className="d-flex align-items-center justify-content-center rounded-circle" style={{
          width: 24, height: 24, fontSize: 11, fontWeight: 700, flexShrink: 0,
          background: s.status === 'done' ? 'var(--green)' : s.status === 'active' ? 'var(--teal)' : 'var(--brd)',
          color: s.status === 'done' || s.status === 'active' ? '#fff' : 'var(--t2)',
        }}>
          {s.status === 'done' ? '✓' : s.id}
        </div>
        <div className="flex-grow-1">
          <div style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--navy)' }}>Step {s.id} — {s.title}</div>
          <div style={{ fontSize: '10.5px', color: 'var(--t3)' }}>{s.sub}</div>
        </div>
        {statusPill}
        <span style={{ color: 'var(--t3)', fontSize: 18, transition: 'transform .2s', transform: isOpen ? 'rotate(90deg)' : 'none' }}>›</span>
      </div>

      {/* Body */}
      {isOpen && s.status !== 'locked' && (
        <div className="p-3 border-top" style={{ borderColor: 'var(--brd2)', background: 'var(--card)' }}>
          {s.id === 1 && <Step1Body s={s} />}
          {s.id === 2 && <Step2Body s={s} />}
          {s.id === 3 && <Step3Body />}
          {s.id === 4 && <Step4Body s={s} onApprove={onApprove} userName={userName} />}
          {s.id === 5 && <Step5Body />}
        </div>
      )}
    </div>
  );
}

function Step1Body({ s }) {
  return (
    <>
      <div className="p-3 rounded-3 mb-3" style={{ background: 'linear-gradient(135deg,var(--teal3),var(--purple2))', border: '1px solid var(--teal4)', fontSize: 12, color: 'var(--navy)', lineHeight: 1.6 }}>
        ✦ <strong style={{ color: 'var(--teal)' }}>Nova data snapshot (Q1 2026):</strong> NL ARB market <strong>€6.2M</strong> (+8.2% YoY). 2 competitors delisted. NHG M62 confirms ARB preferred. CBG: 3 active MAs.
      </div>
      <div className="row g-2 mb-3">
        {[
          { label: 'NL Market', value: '€6.2M', color: 'var(--navy)' },
          { label: 'YoY Growth', value: '+8.2%', color: 'var(--green)' },
          { label: 'Competitors', value: '4', color: 'var(--amber)' },
          { label: 'Tender %', value: '62%', color: 'var(--teal)' },
        ].map((m, i) => (
          <div className="col-6 col-md-3" key={i}>
            <div className="p-2 text-center rounded-2" style={{ background: 'var(--surf)' }}>
              <div style={{ fontSize: '9.5px', color: 'var(--t3)' }}>{m.label}</div>
              <div style={{ fontSize: 19, fontWeight: 800, color: m.color, fontFamily: "'Syne',sans-serif" }}>{m.value}</div>
            </div>
          </div>
        ))}
      </div>
      {s.approvedBy && <div style={{ fontSize: '11.5px', color: 'var(--green)', fontWeight: 500 }}>✓ Approved by {s.approvedBy} · {s.approvedDate}</div>}
    </>
  );
}

function Step2Body({ s }) {
  return (
    <>
      <div className="p-3 rounded-3 mb-3" style={{ background: 'linear-gradient(135deg,var(--teal3),var(--purple2))', border: '1px solid var(--teal4)', fontSize: 12, color: 'var(--navy)', lineHeight: 1.6 }}>
        ✦ <strong style={{ color: 'var(--teal)' }}>Recalculated with Farminform Q1 2026.</strong> Y1 revised from €2.4M → <strong>€2.6M</strong> (+€180K). Dutch number notation applied.
      </div>
      <div className="table-responsive mb-3">
        <table className="tbl" style={{ fontSize: 12 }}>
          <thead><tr><th>Parameter</th><th>Y1 2026</th><th>Y2 2027</th><th>Y3 2028</th><th>Y4 2029</th></tr></thead>
          <tbody>
            {FIN_ROWS.map((r, i) => (
              <tr key={i}>
                <td>{r.param}</td>
                {r.vals.map((v, j) => <td key={j} style={{ fontFamily: "'DM Mono'" }}>{v}</td>)}
              </tr>
            ))}
            <tr style={{ background: 'var(--green2)' }}>
              <td><strong>{NET_ROW.param}</strong></td>
              {NET_ROW.vals.map((v, j) => <td key={j} style={{ fontFamily: "'DM Mono'", fontWeight: 700, color: 'var(--green)' }}>{v}</td>)}
            </tr>
          </tbody>
        </table>
      </div>
      {s.approvedBy && <div style={{ fontSize: '11.5px', color: 'var(--green)', fontWeight: 500 }}>✓ Approved by {s.approvedBy} · {s.approvedDate}</div>}
    </>
  );
}

function Step3Body() {
  return (
    <div className="d-flex flex-column gap-2">
      {EXPERTS.map((e, i) => (
        <div key={i} className="d-flex align-items-start gap-3 p-3 rounded-2" style={{ background: 'var(--green2)' }}>
          <div className="d-flex align-items-center justify-content-center rounded-2 text-white" style={{ width: 30, height: 30, background: e.bg, fontWeight: 700, fontSize: '10.5px', flexShrink: 0 }}>{e.av}</div>
          <div className="flex-grow-1">
            <div style={{ fontSize: '12.5px', fontWeight: 600 }}>{e.name}</div>
            <div style={{ fontSize: '11.5px', color: 'var(--t2)' }}>{e.note}</div>
          </div>
          <span className="pill p-g ms-auto" style={{ flexShrink: 0 }}>✓ GO</span>
        </div>
      ))}
    </div>
  );
}

function Step4Body({ s, onApprove, userName }) {
  if (s.status === 'done') {
    return (
      <div className="p-3 rounded-2" style={{ background: 'var(--green2)', fontSize: '11.5px', color: 'var(--green)', fontWeight: 600 }}>
        ✓ MD Approval granted — {userName} · All departments notified by Nova
      </div>
    );
  }
  return (
    <>
      <div className="p-3 rounded-3 mb-3" style={{ background: 'linear-gradient(135deg,var(--teal3),var(--purple2))', border: '1px solid var(--teal4)', fontSize: 12, color: 'var(--navy)', lineHeight: 1.6 }}>
        ✦ <strong style={{ color: 'var(--teal)' }}>Nova recommendation: GO.</strong> All expert inputs positive. BC revised upward after Farminform Q1. Competitive window open — 2 competitors delisted Q1 2026.
      </div>
      <div className="p-3 rounded-3" style={{ border: '2px solid var(--gold)', background: 'var(--gold2)' }}>
        <div className="d-flex gap-3">
          <div style={{ fontSize: 26, flexShrink: 0, marginTop: 2 }}>🏛</div>
          <div className="flex-grow-1">
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)', fontFamily: "'Syne',sans-serif", marginBottom: 4 }}>Managing Director Approval Required</div>
            <div style={{ fontSize: 12, color: 'var(--t2)', lineHeight: 1.6, marginBottom: 10 }}>Your approval activates the Portfolio Committee workflow. Nova notifies all departments automatically.</div>
            <div className="d-flex flex-wrap gap-2 mb-3">
              {[
                { av: 'MV', bg: 'var(--green)', label: 'RA ✓' },
                { av: 'JP', bg: 'var(--purple)', label: 'Commercial ✓' },
                { av: 'SL', bg: 'var(--amber)', label: 'Legal ✓' },
                { av: 'EK', bg: 'var(--gold)', label: 'MD — You', highlight: true },
              ].map((sig, i) => (
                <div key={i} className="d-flex align-items-center gap-2 px-3 py-1 rounded-2" style={{ background: sig.highlight ? 'var(--gold2)' : '#fff', border: `1px solid ${sig.highlight ? 'var(--gold)' : 'var(--brd)'}`, fontSize: '11.5px' }}>
                  <div className="d-flex align-items-center justify-content-center rounded-2 text-white" style={{ width: 22, height: 22, background: sig.bg, fontSize: '9.5px', fontWeight: 700 }}>{sig.av}</div>
                  <span style={{ fontWeight: sig.highlight ? 700 : 400 }}>{sig.label}</span>
                </div>
              ))}
            </div>
            <div className="d-flex flex-wrap gap-2">
              <button className="btn btn-sm" style={{ background: 'var(--green)', color: '#fff' }} onClick={onApprove}>✓ Approve — GO</button>
              <button className="btn btn-sm" style={{ background: 'var(--red2)', color: 'var(--red)', border: '1.5px solid rgba(214,59,10,.25)' }}>✗ No-Go</button>
              <button className="btn btn-s btn-sm">Add Comment</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Step5Body() {
  return (
    <div className="p-3 rounded-3" style={{ background: 'linear-gradient(135deg,var(--teal3),var(--purple2))', border: '1px solid var(--teal4)', fontSize: 12, color: 'var(--navy)', lineHeight: 1.6 }}>
      ✦ <strong style={{ color: 'var(--teal)' }}>Business Case approved!</strong> Nova has:<br />
      • Created Valsartan project in Pipeline (Negotiate stage)<br />
      • Notified Commercial (Jan) · Legal (Sophie) · RA (Maria)<br />
      • Drafted initial term sheet outline<br />
      • Scheduled Sandoz NL call for Mar 25 2026
      <div className="d-flex flex-wrap gap-2 mt-3">
        <Link to="/pipeline" className="btn btn-p btn-sm">→ View in Pipeline</Link>
        <button className="btn btn-s btn-sm">↓ Export BC Excel</button>
      </div>
    </div>
  );
}
